/**
 * Форматирование денег по локали интерфейса (DESIGN_SYSTEM §3.2, FE-579).
 *
 * Разделитель тысяч и позиция символа валюты берутся из `Intl.NumberFormat`,
 * а не из хардкода: `28.900 €` (de), `28 900 €` (ru), `€28,900` (en). Локаль —
 * **язык интерфейса**, а не браузера: `Intl.NumberFormat(undefined, …)` давал
 * немецкому интерфейсу в английском браузере `€18,223.85`.
 *
 * Вход — **центы** (`price_cents BIGINT`, CLAUDE.md §5). Деления на 100 в
 * компонентах быть не должно: именно там `float` для денег и заводится. И
 * собственных копий этой функции в `features/` тоже: их было семь, и каждая
 * решала по-своему, показывать ли центы.
 */

/**
 * Сколько знаков после запятой.
 *
 * * `cents` — всегда два: цена услуги и счёт («19,90 €», «100,00 €»);
 * * `auto` — центы, только если они есть, как у строки сервера: цены
 *   объявления, предложения, максимум покупателя, разница с объявлением
 *   («28.900 €», «18.223,85 €», «1.400 €» вместо «1.400,00 €»).
 *
 * Округления до целых евро нет намеренно: разница с объявлением — база
 * вознаграждения, и округлённая база не сходилась бы с суммой рядом с ней.
 */
export type MoneyPrecision = 'cents' | 'auto';

export function money(
  cents: number,
  currency: string,
  locale: string,
  precision: MoneyPrecision = 'cents',
): string {
  const digits = precision === 'auto' && cents % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(cents / 100);
}

/** Сумма из ответа API: центы, валюта и строка, собранная сервером. */
export interface MoneyValue {
  amount_cents: number;
  currency: string;
  /** «28 900 €» — без учёта языка интерфейса; только запасной вариант. */
  formatted: string;
}

/**
 * Сумма из ответа API — в локали интерфейса.
 *
 * Строку `formatted` сервер собирает одинаково для всех языков («18 223,85 €»
 * с пробелом вместо разделителя тысяч), поэтому показывать её как есть нельзя.
 * Она нужна только тогда, когда `Intl` не знает валюту, — вместо исключения
 * посреди рендера.
 */
export function moneyOf(
  value: MoneyValue,
  locale: string,
  precision: MoneyPrecision = 'auto',
): string {
  try {
    return money(value.amount_cents, value.currency, locale, precision);
  } catch {
    return value.formatted;
  }
}
